import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { isAxiosError } from 'axios';

import { AuthContext, AuthProvider } from '@/auth/context.js';
import { E2ETestAuthInit } from '@/auth/e2e-test-auth.js';

// Import the generated route tree
import { LDProvider } from '@/components/LDProvider.js';
import { MaintenancePage } from '@/components/maintenance-page.js';
import { CsrfProvider } from '@/lib/csrf-context.js';
import { getEnvironmentLevel } from '@/lib/common-utils.js';
import { WithAuthRole } from '@airreading/common';
import { useFeatureFlag } from '@airreading/feature-flags/web';
import { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { LoaderIcon } from 'lucide-react';
import { routeTree } from './routeTree.gen.js';

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    actingUser: undefined as WithAuthRole | undefined,
  },
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry(failureCount, error) {
        if (failureCount >= 3) return false;
        if (isAxiosError(error)) {
          // handle all non-retry cases.
          if (error.response?.status === 404) {
            return false;
          }
        }
        return true;
      },
    },
  },
});

function AppWithRouter() {
  const { actingUser, loading } = useContext(AuthContext);
  const context = useMemo(() => ({ actingUser }), [actingUser]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center p-10">
        <div className="flex justify-center">
          <LoaderIcon />
        </div>
      </div>
    );
  }

  return <RouterProvider router={router} context={context} />;
}

/**
 * Replaces the whole app with a maintenance screen when the `release-maintenance-page`
 * flag is on. Defaults to `false` so normal usage is never interrupted.
 */
function MaintenanceGate({ children }: PropsWithChildren) {
  const showMaintenancePage = useFeatureFlag('release-maintenance-page', false);

  if (showMaintenancePage) {
    return <MaintenancePage />;
  }

  return <>{children}</>;
}

function App() {
  const [allowE2ETestAuth, setAllowE2ETestAuth] = useState(false);
  useEffect(() => {
    setAllowE2ETestAuth(typeof window !== 'undefined' && getEnvironmentLevel() !== 'prod');
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CsrfProvider>
        <AuthProvider>
          {allowE2ETestAuth && <E2ETestAuthInit />}
          <LDProvider>
            <MaintenanceGate>
              <AppWithRouter />
            </MaintenanceGate>
          </LDProvider>
        </AuthProvider>
      </CsrfProvider>
    </QueryClientProvider>
  );
}

export default App;
